import posthog from 'posthog-js';

import { isShare } from './App';
import { getCurrentUser } from './Auth';
import { getUserSetting } from './Settings';
import { isValidEmail } from './Validators';

export const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';

export const slugify = text => (
  text.toString().toLowerCase().trim()
    .replace(/[^\w\s-]/g, '')
    .replace(/[\s_-]+/g, '-')
    .replace(/^-+|-+$/g, '')
);

export const slugifyUrl = text => (
  text.toString().toLowerCase().trim()
    .replace(/[^\w\s^-]/g, '')
    .replace(/[\s_-]+/g, '-')
);

export const capitalize = string => (
  string.charAt(0).toUpperCase() + string.slice(1)
);

/* eslint-disable */
export const nl2br = (str, isXhtml) => {
  if (typeof str === 'undefined' || str === null) {
    return '';
  }

  const breakTag = (isXhtml || typeof isXhtml === 'undefined') ? '<br ' + '/>' : '<br>';

  return (str + '').replace(/(\r\n|\n\r|\r|\n)/g, breakTag + '$1');
};
/* eslint-enable */

export const pluralize = (text, number) => (
  `${text}${number === 1 ? '' : 's'}`
);

export const parseResponseErrors = (response) => {
  const {
    status,
    data,
  } = response;

  const messages = {};

  if (!status) {
    return messages;
  }

  if (data) {
    if (data.errors) {
      const { errors } = data;
      Object.keys(errors).forEach((key) => {
        messages[key] = errors[key].join(', ');
      });
      return messages;
    }
    if (data.error && data.message) {
      messages.error = data.message;
    }
  }

  return messages;
};

export const scrollModalToError = (modalClass) => {
  setTimeout(() => {
    const modalBody = document.querySelector(`.${modalClass} .modal-body`);
    if (modalBody) {
      const error = modalBody.getElementsByClassName('is-invalid');
      if (error.length > 0) {
        modalBody.scrollTo({ top: error[0].offsetTop - 30, left: 0, behavior: 'smooth' });
      }
    }
  }, 200);
};

export const isNumber = string => (
  /^\d+$/.test(string)
);

export const formatNumber = (v, decimalPlaces = 0) => (
  Math.round(v, decimalPlaces)
);

export const camelToSnakeCase = str => str.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`);

export const capitalizeFirstLetter = (string) => (
  string.charAt(0).toUpperCase() + string.slice(1)
);

export const getVisibleItemsText = (page, perPage, total) => {
  const from = (page - 1) * perPage + 1;
  const to = (page - 1) * perPage + perPage;

  if (total === 0) {
    return `${0}`;
  }

  return `${from} - ${Math.min(to, total)}`;
};

export const arrayMove = (arr, from, to) => (
  arr.reduce((prev, current, idx, self) => {
    if (from === to) {
      prev.push(current);
    }
    if (idx === from) {
      return prev;
    }
    if (from < to) {
      prev.push(current);
    }
    if (idx === to) {
      prev.push(self[from]);
    }
    if (from > to) {
      prev.push(current);
    }
    return prev;
  }, [])
);

export const showZendeskWebWidget = () => {
  if (window.zE) {
    window.zE('webWidget', 'show');
  }
};

export const hideZendeskWebWidget = () => {
  if (window.zE) {
    window.zE('webWidget', 'hide');
  }
};

export const pingIntercom = () => {
  if (window.Intercom) {
    window.Intercom('update');
  }
};

export const showIntercomWidget = () => {
  if (window.Intercom && !isShare()) {
    window.Intercom('update', { hide_default_launcher: false });
  }
};

export const hideIntercomWidget = () => {
  if (window.Intercom) {
    window.Intercom('update', { hide_default_launcher: true });
  }
};

export const openIntercomWidget = (space = null) => {
  if (window.Intercom) {
    if (space) {
      window.Intercom('showSpace', space);
    } else {
      window.Intercom('show');
    }
  }
};

export const openIntercomNewMessage = () => {
  if (window.Intercom && !isShare()) {
    window.Intercom('showNewMessage');
  }
};

const getUserRole = (profile) => {
  if (profile.role) {
    return profile.role.name;
  }
  if (profile.is_superadmin) {
    return 'Superadmin';
  }
  if (profile.is_admin) {
    return 'Admin';
  }
  if (profile.is_manager) {
    return 'Manager';
  }
  return 'User';
};

const getUserOnboardingSteps = () => {
  const steps = {};

  const onboardingSteps = getUserSetting('onboarding_steps') || [];
  onboardingSteps.forEach((step) => {
    if (step.slug !== 'initial-step') {
      let status = null;
      if (step.skipped) {
        status = 'skipped';
      } else if (step.completed) {
        status = 'complete';
      }
      steps[`Onboarding: ${step.slug.replaceAll('-', ' ')}`] = status;
    }
  });

  return steps;
};

const getIntegrationsAndImporters = (profile) => {
  const integrations = profile.integrations ?
    profile.integrations.map(integration => `"${integration.id}"`) : [];
  const importers = profile.importers ?
    profile.importers.filter(importer => (importer.is_connected)).map(importer => `"${importer.id}"`) : [];
  return [...integrations, ...importers].join(',');
};

export const updateIntercomUser = (profile) => {
  if (window.Intercom) {
    if (profile && profile.uuid) {
      const args = {
        user_id: profile.uuid,
        email: profile.email,
        name: `${profile.first_name || ''} ${profile.last_name || ''}`,
        created_at: profile.created_at,
        has_logged_in: true,
        activation_token: null,
        g2fa_enabled: profile.g2fa_enabled,
        integrations: getIntegrationsAndImporters(profile),
      };

      if (!profile.is_superadmin) {
        args.instance_url = window.location.hostname;
      }

      if (profile.intercom_user_hash) {
        args.user_hash = profile.intercom_user_hash;
      }

      if (profile.avatar_url) {
        args.avatar = {
          type: 'avatar',
          image_url: profile.avatar_url,
        };
        args.has_profile_image = true;
      } else {
        args.has_profile_image = false;
      }

      args.role = getUserRole(profile);

      if (profile.founder) {
        args.founder = true;
      } else {
        args.founder = false;
      }

      const { client } = profile;
      if (client && !profile.is_superadmin) {
        args.company = {
          company_id: client.uuid,
          created_at: client.created_at,
          name: client.name || client.subdomain,
          type: client.type,
          has_logo: client.has_logo,
          subscribed: client.is_subscribed,
          subscription_next_payment_at: client.next_payment_at,
          is_active: client.is_active,
          is_free: client.is_free,
          is_complimentary: client.is_complimentary,
          is_in_trial: client.is_in_trial,
          used_storage_percentage: client.used_storage_percentage,
          plan: client.plan_name,
          plan_storage: client.file_size_limit ?
            (client.file_size_limit / 1024 / 1024 / 1024) :
            null,
          has_duplicates: client.has_duplicates,
        };
      }

      window.Intercom('update', args);
    }
  }
};

export const sendIntercomEvent = (event, data = {}) => {
  if (window.Intercom) {
    window.Intercom('trackEvent', event, data);
  }
};

export const sendMixpanelEvent = (event, data = {}) => {
  if (window.mixpanel) {
    const mixpanelEvent = capitalizeFirstLetter(event.replaceAll('-', ' '));
    window.mixpanel.track(mixpanelEvent, data);
  }
};

export const sendPostHogEvent = (event, data = {}) => {
  if (posthog) {
    const posthogEvent = capitalizeFirstLetter(event.replaceAll('-', ' '));
    posthog.capture(posthogEvent, data);
  }
};

export const sendSegmentEvent = (event, data = {}) => {
  if (window.analytics) {
    const segmentEvent = capitalizeFirstLetter(event.replaceAll('-', ' '));
    window.analytics.track(segmentEvent, data);
  }
};

export const sendTrackingEvent = (event, data = {}) => {
  sendIntercomEvent(event, data);
  sendPostHogEvent(event, data);
};

export const checkIntercomTour = () => {
  if (window.Intercom) {
    const params = (new URL(document.location)).searchParams;
    if (params.get('tour_id')) {
      window.Intercom('startTour', params.get('tour_id'));
    }
  }
};

export const setRollbarPerson = (profile = null) => {
  if (!window.Rollbar) {
    return;
  }

  const user = profile || getCurrentUser();
  if (!(user && user.uuid)) {
    return;
  }

  window.Rollbar.configure({
    payload: {
      person: {
        id: user.uuid,
      },
    },
  });
};

export const setSegmentUser = (profile = null) => {
  if (!window.analytics) {
    return;
  }

  const user = profile || getCurrentUser();
  if (!(user && user.uuid)) {
    return;
  }

  window.analytics.identify(user.uuid, {
    email: user.email,
    name: user.name,
  });
};

export const sendPageEventToSegment = (location, prevLocation) => {
  if (!window.analytics) {
    return;
  }

  window.analytics.page({
    url: `${window.location.origin}${location.pathname}${location.search}`,
    referrer: `${window.location.origin}${prevLocation.pathname}${prevLocation.search}`,
  });
};

export const setMixpanelUser = (profile = null) => {
  if (!window.mixpanel) {
    return;
  }

  const user = profile || getCurrentUser();
  if (!(user && user.uuid)) {
    return;
  }
  window.mixpanel.identify(user.uuid);

  const args = {
    $email: profile.email,
    $first_name: profile.first_name,
    $last_name: profile.last_name,
    $created: profile.created_at,
    '2FA enabled': profile.g2fa_enabled,
    Founder: profile.founder,
    Integrations: getIntegrationsAndImporters(profile),
  };
  args.Role = getUserRole(profile);
  const { client } = profile;
  if (profile.role !== 'superadmin' && client) {
    args.Plan = client.plan_name;
    args.Free = client.is_free;
    args.Complimentary = client.is_complimentary;
  }
  window.mixpanel.people.set(args);
};

export const setPostHogUser = (profile = null) => {
  if (!posthog) {
    return;
  }

  const user = profile || getCurrentUser();
  if (!(user && user.uuid)) {
    return;
  }

  const args = {
    Email: profile.email,
    'First name': profile.first_name,
    'Last name': profile.last_name,
    'Signed up': profile.created_at,
    '2FA enabled': profile.g2fa_enabled,
    Founder: profile.founder,
    Integrations: getIntegrationsAndImporters(profile),
  };
  args.Role = getUserRole(profile);
  const { client } = profile;
  if (profile.role !== 'superadmin' && client) {
    args.Plan = client.plan_name;
    args.Free = client.is_free;
    args.Complimentary = client.is_complimentary;
  }
  const onboardingSteps = getUserOnboardingSteps(profile);
  args['Onboarding complete'] = !!Object.keys(onboardingSteps).length >= 6;
  const cargs = {
    ...args,
    ...onboardingSteps,
  };

  posthog.identify(user.uuid, cargs);
};

export const getUrlParam = (param) => {
  const params = (new URL(document.location)).searchParams;
  return params.get(param);
};

export const removeUrlParam = (param) => {
  const params = (new URL(document.location)).searchParams;
  params.delete(param);
  window.history.replaceState(null, '', `${document.location.pathname}?${params.toString()}`);
};

export const removeUrlParamFromUrl = (url, param) => {
  const r = new URL(url);
  r.searchParams.delete(param);
  return r.href;
};

export const getTabUrlParam = () => {
  const params = (new URL(document.location)).searchParams;
  return params.get('tab');
};

export const updateTabUrlParam = (tab) => {
  const params = (new URL(document.location)).searchParams;
  params.set('tab', tab);
  window.history.replaceState(null, '', `${document.location.pathname}?${params.toString()}`);
};

export const getEmailFromParam = () => {
  const email = getUrlParam('email');
  if (email && isValidEmail(email)) {
    return email;
  }
  return '';
};

export const isImportModalOpen = () => (
  (new URLSearchParams(window.location.search)).get('importer')
);

export const getStateFromUrl = () => {
  const state = new URLSearchParams(window.location.search).get('state');
  if (!state) return null;

  try {
    return JSON.parse(atob(state));
  } catch (e) {
    return state;
  }
};

export const strikeThrough = (text) => (
  text
    .split('')
    .map(char => `${char}\u0336`)
    .join('')
);

export const detectOS = () => {
  const { userAgent, platform } = window.navigator;
  const macosPlatforms = ['Macintosh', 'MacIntel', 'MacPPC', 'Mac68K'];
  const windowsPlatforms = ['Win32', 'Win64', 'Windows', 'WinCE'];
  const iosPlatforms = ['iPhone', 'iPad', 'iPod'];
  let os = null;

  if (macosPlatforms.indexOf(platform) !== -1) {
    os = 'macOS';
  } else if (iosPlatforms.indexOf(platform) !== -1) {
    os = 'iOS';
  } else if (windowsPlatforms.indexOf(platform) !== -1) {
    os = 'Windows';
  } else if (/Android/.test(userAgent)) {
    os = 'Android';
  } else if (!os && /Linux/.test(platform)) {
    os = 'Linux';
  }

  return os;
};

export const isSafari = () => (/^((?!chrome|android).)*safari/i.test(navigator.userAgent));
